/**
 * Formula core for the Excel widget — `@A1` cell references.
 *
 * Everything here is pure and synchronous. Resolving a reference means
 * finding the referenced cell's *formula*, not its value, so the whole
 * dependency graph (cycle detection, composition) needs no network.
 *
 * A cell body is classified by one rule: after tokenizing, if any text
 * survives that is not a reference, number, operator or paren, the cell
 * has English and needs the DSL agent. Otherwise it is deterministic
 * and must never reach an LLM.
 *
 * See docs/market-monitor/excel_cell_references.md.
 */

import {
  cellAddress,
  columnIndex,
  isDslCell,
  parseLiteralNumber,
  promptOf,
  type ExcelWidgetConfig,
} from './excelConfig';

export type FormulaErrorCode =
  | 'empty_formula'
  | 'malformed_ref'
  | 'ref_out_of_range'
  | 'circular_reference'
  | 'empty_reference'
  | 'non_numeric_reference'
  | 'translation_altered';

export interface FormulaError {
  code: FormulaErrorCode;
  message: string;
}

export interface CellRef {
  r: number;
  c: number;
  label: string;
}

export type FormulaToken =
  | { kind: 'ref'; ref: CellRef; start: number; end: number }
  | { kind: 'number'; text: string; start: number; end: number }
  | { kind: 'operator'; text: string; start: number; end: number }
  | { kind: 'text'; text: string; start: number; end: number };

export interface ParsedFormula {
  tokens: FormulaToken[];
  /** Deduped, in order of first appearance. Index is the REFn number. */
  refs: CellRef[];
  hasEnglish: boolean;
}

export type ParseResult =
  | { kind: 'ok'; parsed: ParsedFormula }
  | { kind: 'error'; error: FormulaError };

/**
 * `pending` means a referenced cell is valid but has not been translated
 * yet — an ordering state, not a failure. It must never be shown as an
 * error or the sheet flashes red while upstream cells settle.
 */
export type CellExpression =
  | { kind: 'ok'; dsl: string }
  | { kind: 'pending' }
  | { kind: 'error'; error: FormulaError };

const OPERATORS = new Set(['+', '-', '*', '/', '(', ')']);
const REF_PATTERN = /^@([A-Za-z]+)([1-9][0-9]*)/;
const NUMBER_PATTERN = /^(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)/;
const PLACEHOLDER_SOURCE = 'REF([0-9]+)';

const err = (code: FormulaErrorCode, message: string): FormulaError => ({
  code,
  message,
});

const placeholderFor = (index: number): string => `REF${index}`;

/** The offending `@…` snippet, for a malformed-reference message. */
const malformedSnippet = (body: string, at: number): string => {
  let i = at + 1;
  while (i < body.length && /[A-Za-z0-9]/.test(body[i])) i += 1;
  return body.slice(at, i);
};

/**
 * Split a cell body into references, numbers, operators and leftover
 * text. A malformed `@` is a hard error rather than leftover text — a
 * typo must never fall through to the AI path (D5).
 */
export function tokenizeFormula(body: string): ParseResult {
  const tokens: FormulaToken[] = [];
  let i = 0;

  while (i < body.length) {
    const ch = body[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (ch === '@') {
      const match = REF_PATTERN.exec(body.slice(i));
      if (!match) {
        const snippet = malformedSnippet(body, i);
        return {
          kind: 'error',
          error: err(
            'malformed_ref',
            `"${snippet}" is not a valid cell reference.`,
          ),
        };
      }
      const c = columnIndex(match[1]);
      const r = Number(match[2]) - 1;
      tokens.push({
        kind: 'ref',
        ref: { r, c, label: cellAddress(r, c) },
        start: i,
        end: i + match[0].length,
      });
      i += match[0].length;
      continue;
    }

    if (OPERATORS.has(ch)) {
      tokens.push({ kind: 'operator', text: ch, start: i, end: i + 1 });
      i += 1;
      continue;
    }

    const numberMatch = NUMBER_PATTERN.exec(body.slice(i));
    if (numberMatch) {
      tokens.push({
        kind: 'number',
        text: numberMatch[0],
        start: i,
        end: i + numberMatch[0].length,
      });
      i += numberMatch[0].length;
      continue;
    }

    const start = i;
    while (i < body.length && !OPERATORS.has(body[i]) && body[i] !== '@') {
      i += 1;
    }
    const text = body.slice(start, i);
    if (text.trim().length > 0) {
      tokens.push({ kind: 'text', text, start, end: i });
    }
  }

  const refs: CellRef[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    if (token.kind !== 'ref') continue;
    const key = `${token.ref.r}:${token.ref.c}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(token.ref);
  }

  return {
    kind: 'ok',
    parsed: {
      tokens,
      refs,
      hasEnglish: tokens.some((t) => t.kind === 'text'),
    },
  };
}

/** Tokenize the body of a `=` cell. Empty bodies are an error. */
export function parseFormulaBody(body: string | null): ParseResult {
  if (body === null || body.trim() === '') {
    return {
      kind: 'error',
      error: err('empty_formula', 'Cell starts with "=" but has no formula.'),
    };
  }
  return tokenizeFormula(body);
}

export type FormulaMode = 'deterministic' | 'ai' | 'invalid';

/**
 * Whether a cell body needs the DSL agent. `invalid` means it will not
 * run at all, so it is neither. Used for the cell's mode indicator.
 */
export function formulaMode(body: string | null): FormulaMode {
  const result = parseFormulaBody(body);
  if (result.kind === 'error') return 'invalid';
  return result.parsed.hasEnglish ? 'ai' : 'deterministic';
}

const refIndexOf = (refs: CellRef[], r: number, c: number): number =>
  refs.findIndex((ref) => ref.r === r && ref.c === c);

/** Rebuild a deterministic body with each reference replaced by its DSL. */
const composeDeterministic = (
  parsed: ParsedFormula,
  resolved: Map<string, string>,
): string =>
  parsed.tokens
    .map((token) => {
      if (token.kind !== 'ref') return token.text;
      return `(${resolved.get(`${token.ref.r}:${token.ref.c}`)})`;
    })
    .join(' ');

/** Replace REF0..REFn in a cached template with each reference's DSL. */
const applyTemplate = (
  template: string,
  refs: CellRef[],
  resolved: Map<string, string>,
): { kind: 'ok'; dsl: string } | { kind: 'error'; error: FormulaError } => {
  const scanner = new RegExp(PLACEHOLDER_SOURCE, 'g');
  let match = scanner.exec(template);
  while (match !== null) {
    if (!refs[Number(match[1])]) {
      return {
        kind: 'error',
        error: err(
          'translation_altered',
          `Cached translation refers to ${match[0]}, which this formula does not have.`,
        ),
      };
    }
    match = scanner.exec(template);
  }

  const dsl = template.replace(
    new RegExp(PLACEHOLDER_SOURCE, 'g'),
    (_all, digits: string) => {
      const ref = refs[Number(digits)];
      return `(${resolved.get(`${ref.r}:${ref.c}`)})`;
    },
  );
  return { kind: 'ok', dsl };
};

interface ResolveContext {
  config: ExcelWidgetConfig;
  path: string[];
  labels: string[];
}

const resolveAt = (ctx: ResolveContext, r: number, c: number): CellExpression => {
  const { config } = ctx;
  const label = cellAddress(r, c);
  const depth = ctx.path.length;

  if (r < 0 || r >= config.rows || c < 0 || c >= config.cols) {
    return {
      kind: 'error',
      error: err('ref_out_of_range', `@${label} is outside this sheet.`),
    };
  }

  const key = `${r}:${c}`;
  if (ctx.path.includes(key)) {
    const cycle = [...ctx.labels.slice(ctx.path.indexOf(key)), label].join(' → ');
    return {
      kind: 'error',
      error: err('circular_reference', `Circular reference: ${cycle}.`),
    };
  }

  const cell = config.cells[r][c];

  if (!isDslCell(cell)) {
    if (cell.raw.trim() === '') {
      return {
        kind: 'error',
        error: err('empty_reference', `Cell ${label} is empty.`),
      };
    }
    const value = parseLiteralNumber(cell.raw);
    if (value === null) {
      return {
        kind: 'error',
        error: err('non_numeric_reference', `Cell ${label} is not a number.`),
      };
    }
    return { kind: 'ok', dsl: String(value) };
  }

  const parseResult = parseFormulaBody(promptOf(cell));
  if (parseResult.kind === 'error') {
    return depth === 0
      ? parseResult
      : {
          kind: 'error',
          error: err(
            parseResult.error.code,
            `Cell ${label}: ${parseResult.error.message}`,
          ),
        };
  }
  const parsed = parseResult.parsed;

  const childCtx: ResolveContext = {
    config,
    path: [...ctx.path, key],
    labels: [...ctx.labels, label],
  };
  const resolved = new Map<string, string>();
  for (const ref of parsed.refs) {
    const child = resolveAt(childCtx, ref.r, ref.c);
    if (child.kind !== 'ok') return child;
    resolved.set(`${ref.r}:${ref.c}`, child.dsl);
  }

  if (!parsed.hasEnglish) {
    return { kind: 'ok', dsl: composeDeterministic(parsed, resolved) };
  }

  const template = cell.resolvedDsl?.trim();
  if (!template) return { kind: 'pending' };

  const applied = applyTemplate(template, parsed.refs, resolved);
  if (applied.kind === 'error') return applied;
  return { kind: 'ok', dsl: applied.dsl };
};

/**
 * The executable DSL for a cell — references inlined, cached
 * translations applied. Pure: safe to call from a render path or from
 * the dashboard provider.
 */
export function composeCellDsl(
  config: ExcelWidgetConfig,
  r: number,
  c: number,
): CellExpression {
  return resolveAt({ config, path: [], labels: [] }, r, c);
}

export interface InlinedSpan {
  text: string;
  refIndex: number;
}

export interface TranslationRequest {
  /** Cell body with every reference replaced by its DSL, English intact. */
  prompt: string;
  /** Each inlined span in order of occurrence, for verify + re-mask. */
  spans: InlinedSpan[];
  refs: CellRef[];
}

export type TranslationRequestResult =
  | { kind: 'ok'; request: TranslationRequest }
  | { kind: 'pending' }
  | { kind: 'error'; error: FormulaError };

/**
 * Build the prompt for an English cell. References are inlined so the
 * agent sees real expressions; everything else is preserved verbatim so
 * the English reaches the model exactly as the user wrote it.
 */
export function buildTranslationRequest(
  config: ExcelWidgetConfig,
  r: number,
  c: number,
): TranslationRequestResult {
  const cell = config.cells[r]?.[c];
  if (!cell || !isDslCell(cell)) {
    return {
      kind: 'error',
      error: err('empty_formula', `Cell ${cellAddress(r, c)} is not a formula.`),
    };
  }

  const parseResult = parseFormulaBody(promptOf(cell));
  if (parseResult.kind === 'error') return parseResult;
  const parsed = parseResult.parsed;

  const ctx: ResolveContext = { config, path: [`${r}:${c}`], labels: [cellAddress(r, c)] };
  const resolved = new Map<string, string>();
  for (const ref of parsed.refs) {
    const child = resolveAt(ctx, ref.r, ref.c);
    if (child.kind !== 'ok') return child;
    resolved.set(`${ref.r}:${ref.c}`, child.dsl);
  }

  const body = promptOf(cell) as string;
  const spans: InlinedSpan[] = [];
  let prompt = '';
  let cursor = 0;
  for (const token of parsed.tokens) {
    if (token.kind !== 'ref') continue;
    const text = `(${resolved.get(`${token.ref.r}:${token.ref.c}`)})`;
    prompt += body.slice(cursor, token.start) + text;
    cursor = token.end;
    spans.push({ text, refIndex: refIndexOf(parsed.refs, token.ref.r, token.ref.c) });
  }
  prompt += body.slice(cursor);

  return { kind: 'ok', request: { prompt, spans, refs: parsed.refs } };
}

const squeezeWithMap = (s: string): { squeezed: string; map: number[] } => {
  let squeezed = '';
  const map: number[] = [];
  for (let i = 0; i < s.length; i += 1) {
    if (/\s/.test(s[i])) continue;
    squeezed += s[i];
    map.push(i);
  }
  return { squeezed, map };
};

/**
 * Locate `needle` in `haystack`, tolerating whitespace differences —
 * the agent may legitimately reformat spacing around an expression it
 * was handed. Returns the range in `haystack` coordinates.
 */
export function looseIndexOf(
  haystack: string,
  needle: string,
): [number, number] | null {
  const exact = haystack.indexOf(needle);
  if (exact !== -1) return [exact, exact + needle.length];

  const { squeezed, map } = squeezeWithMap(haystack);
  const target = needle.replace(/\s+/g, '');
  if (target.length === 0) return null;
  const at = squeezed.indexOf(target);
  if (at === -1) return null;
  return [map[at], map[at + target.length - 1] + 1];
}

export type MaskResult =
  | { kind: 'ok'; template: string }
  | { kind: 'error'; error: FormulaError };

/**
 * Verify the agent returned every inlined expression untouched, then
 * rewrite them back to REFn so the cached translation does not depend
 * on what the referenced cells currently contain (D9).
 *
 * A missing span means the agent rewrote part of the formula the user
 * specified exactly — we refuse to run it rather than return a number
 * that quietly means something else.
 */
export function maskTranslation(
  returned: string,
  spans: InlinedSpan[],
): MaskResult {
  let template = returned;
  for (const span of spans) {
    const range = looseIndexOf(template, span.text);
    if (!range) {
      return {
        kind: 'error',
        error: err(
          'translation_altered',
          `Translation changed the cell reference ${span.text} — refusing to run it.`,
        ),
      };
    }
    template =
      template.slice(0, range[0]) +
      placeholderFor(span.refIndex) +
      template.slice(range[1]);
  }
  return { kind: 'ok', template };
}
